import { createFormControl } from "solid-forms";
import { For, Show, createMemo, splitProps } from "solid-js";

import { directives$ } from "../../directives";
//
import type { FormControlData, FormControlValue } from "../SHARED/_model";
import type { MainProps } from "./_model";
import { solidstate } from ":setup/qstate";
import ArrowIcon from "./assets/icons/Vector.svg";

const EMPTY_OPTION = "undefined";
export default function Select<V extends FormControlValue, D extends FormControlData>(props: MainProps<V, D>) {
  const [local, others] = splitProps(props, [
    "control",
    "options",
    "placeholder",
    "selectedClass",
    "selectedGroupClass",
    "iconClass",
    "optionClass",
    "optionGroupClass",
    "multiple",
    "placeholderClass",
    "onClick",
    "onselected",
    "onSelected",
    "onNoOptions",
    "allowClearSelected",
    "clearSelectedClass",
    "emptyOptionAlt",
  ]);
  // @ts-ignore: unused function 'clickOutside'
  const { clickOutside } = directives$.events;
  const _control = Array.isArray(local.control) ? createFormControl<V, D>(...local.control) : local.control;
  const empty_option = local.emptyOptionAlt ?? EMPTY_OPTION;
  const $selected = createMemo(() => {
    if (!local.multiple) return undefined;
    const result = [];
    if (Array.isArray(_control.value)) {
      result.push(..._control.value);
    } else {
      result.push(_control.value);
    }
    return result;
  });
  const $show_options = solidstate.create(false);
  local.placeholder = local.placeholder ?? "Select...";

  // createEffect(() => {
  //   const value = $show_options.value;
  //   console.log("select control is :: ", value);
  // });

  // runs when select input is clicked
  function showOptions(e: Event) {
    e.preventDefault();
    if (!_control.isDisabled) {
      if (local.options != undefined && local.options.length > 0) {
        if ($show_options.value) {
          _control.markTouched(false);
        } else {
          _control.markTouched(true);
        }
        $show_options.set((s) => !s);
      } else {
        local.onNoOptions?.();
      }
    }
    if (local.onClick) {
      (local.onClick as Function)(e);
    }
  }

  function selectOption(e: Event, idx: number, empty_option: boolean) {
    e.preventDefault();
    // check if idx is for the allow clear selected, meaning equals null
    const value = empty_option ? EMPTY_OPTION : local.options[idx];
    _control.setValue(value);
    local.onselected?.(value);
    local.onSelected?.(value);
  }

  function isSelected(item: any) {
    // @ts-ignore
    return _control.value?.includes(item);
  }

  return (
    <section
      {...others}
      use:clickOutside={() => {
        if ($show_options.unwrap) {
          $show_options.set(false);
        }
      }}
      class={`relative flex flex-col cursor-pointer ${others.class ?? ""}`}
      classList={{
        invalid: !!_control.errors,
        touched: _control.isTouched,
        disabled: _control.isDisabled,
        ...others.classList,
      }}
      onClick={(e) => showOptions(e)}
      onblur={() => _control.markTouched(true)}
      onfocus={() => _control.markTouched(true)}
      onfocusin={() => _control.markTouched(true)}
      onfocusout={() => _control.markTouched(false)}
    >
      {/* the main and only input */}
      <input
        type="text"
        // class="w-full h-full bg-red"
        hidden
        value={_control.value as any}
        onblur={() => _control.markTouched(true)}
        required={_control.isRequired}
        disabled={_control.isDisabled}
      />
      {/* user selection and arrow icon  */}
      <div class="relative flex flex-row  w-full h-full  items-center justify-between px-2">
        <div class={`selected-group relative flex flex-row ${local.selectedGroupClass ?? ""}`}>
          {_control.value == undefined ? (
            <span class={`w-full placeholder ${local.placeholderClass ?? ""}`}>
              {local.placeholder && local.placeholder !== "" ? local.placeholder : "Select An Options"}
            </span>
          ) : local.multiple == undefined ? (
            <span class={`whitespace-nowrap selected  ${local.selectedClass ?? ""}`}>
              {_control.value === EMPTY_OPTION ? empty_option : String(_control.value)}
            </span>
          ) : (
            <For each={$selected()}>
              {(item, index) => {
                // const arr_length = $selected().length;

                return <span class={`selected ${local.selectedClass ?? ""}`}>{item}</span>;
              }}
            </For>
          )}
        </div>
        <ArrowIcon class={`w-15px max-w-15px duration-300   ${$show_options.value ? "!rotate-180" : ""}`} />
      </div>
      {/* options */}
      <div
        style={{
          height: "fit-content",
          color: "black",
          "background-color": "white",
          border: "solid 2px gray",
        }}
        class={`option-group
          flex flex-col w-full !z-100 overflow-y-auto self-center
          ${$show_options.value ? "absolute top-106%" : "!hidden "}
          ${local.optionGroupClass ?? " "}
          `}
      >
        <Show when={$show_options.value}>
          <Show when={local.allowClearSelected != undefined}>
            <span
              class={`cursor-pointer option option-clear ${local.optionClass ?? ""} ${local.clearSelectedClass ?? ""}`}
              onclick={(e) => selectOption(e, null, false)}
            >
              {typeof local.allowClearSelected === "string" ? local.allowClearSelected : "Clear"}
            </span>
          </Show>
          <For each={local.options}>
            {(item, index) => {
              const i = index();
              const empty_item = item == undefined || (typeof item === "string" && item.length <= 0);
              return (
                <span
                  class={
                    "cursor-pointer option" + " " + (isSelected(item) ? "chosen" : "") + " " + (local.optionClass ?? "")
                  }
                  onclick={(e) => selectOption(e, i, empty_item)}
                >
                  {empty_item ? empty_option : item}
                </span>
              );
            }}
          </For>
        </Show>
      </div>
    </section>
  );
}
